import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Row, Col, Form, Button, Spinner } from "react-bootstrap";
import 'react-phone-number-input/style.css';
import PhoneInput, { isPossiblePhoneNumber } from 'react-phone-number-input';

import { fetchMyPermissions, updateUser, fetchUserById } from "../../../redux/slices/userSlice";

const NATIONALITIES = [
    "Alemã", "Americana", "Angolana", "Brasileira", "Britânica",
    "Cabo-verdiana", "Espanhola", "Francesa", "Guineense", "Italiana",
    "Moçambicana", "Portuguesa", "Santomense", "Suíça", "Timorense", "Outra"
];

const COUNTRIES = [
    "Alemanha", "Angola", "Brasil", "Cabo Verde", "Espanha",
    "Estados Unidos", "França", "Guiné-Bissau", "Itália", "Moçambique",
    "Portugal", "Reino Unido", "São Tomé e Príncipe", "Suíça", "Timor-Leste", "Outro"
];

const SELF_EDITABLE_FIELDS = new Set([
    "first_name",
    "last_name",
    "phone_number",
    "language",
    "address",
    "home_address",
    "country",
    "region",
    "city",
    "locality",
    "postal_code",
    "emergency_contact_name",
    "emergency_contact_phone",
]);

const FULL_PERSONAL_FIELDS = new Set([
    ...SELF_EDITABLE_FIELDS,
    "date_of_birth",
    "nationality",
    "iban",
    "swift_bic",
    "cc",
    "social_security_number",
    "nif",
]);

const PersonalDetailsTab = ({ currentUser, userId }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const loggedInUser = useSelector((state) => state.auth?.user);
    const permissions = useSelector((state) => state.users?.permissions || {});
    const isOwner = String(loggedInUser?.id) === String(userId);
    const canViewSensitive = permissions.can_view_sensitive_users === true;
    const canUpdateUsers = permissions.can_update_users === true;
    const canManageTarget = canUpdateUsers && (
        !currentUser?.role?.name ||
        currentUser.role.name !== "Super Admin" ||
        permissions.can_delete_users === true
    );
    const canViewFull = canViewSensitive || isOwner;
    const canEdit = canUpdateUsers || isOwner;
    const canEditField = (fieldName) =>
        canManageTarget || (isOwner && SELF_EDITABLE_FIELDS.has(fieldName));

    const [formData, setFormData] = useState({});
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        dispatch(fetchMyPermissions());
    }, [dispatch]);

    useEffect(() => {
        if (currentUser) {
            setFormData({
                first_name: currentUser.first_name || "",
                last_name: currentUser.last_name || "",
                phone_number: currentUser.phone_number ? currentUser.phone_number.replace(/\s+/g, '') : "",
                emergency_contact_phone: currentUser.emergency_contact_phone ? currentUser.emergency_contact_phone.replace(/\s+/g, '') : "",
                date_of_birth: currentUser.date_of_birth ? currentUser.date_of_birth.split('T')[0] : "",
                nationality: currentUser.nationality || "",
                delegation_name: currentUser.delegation?.name || currentUser.delegation_name || "",
                iban: currentUser.iban || "",
                swift_bic: currentUser.swift_bic || "",
                cc: currentUser.cc || "",
                social_security_number: currentUser.social_security_number || "",
                nif: currentUser.nif || "",
                address: currentUser.address || "",
                city: currentUser.city || "",
                postal_code: currentUser.postal_code || "",
                country: currentUser.country || "",
                region: currentUser.region || "",
            });
        }
    }, [currentUser]);

    const isPhoneValid = !formData.phone_number || isPossiblePhoneNumber(formData.phone_number);
    
    const isAdult = (birthDate) => {
        if (!birthDate) return true;
        const today = new Date();
        const dob = new Date(birthDate);
        let age = today.getFullYear() - dob.getFullYear();
        const monthDiff = today.getMonth() - dob.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) age--;
        return age >= 18;
    };

    const isDateValid = isAdult(formData.date_of_birth);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handlePhoneChange = (value, fieldName) => {
        const cleanedValue = value ? value.replace(/\s+/g, '') : "";
        setFormData((prev) => ({ ...prev, [fieldName]: cleanedValue }));
    };

    const handleSave = async (e) => {
        if (e) e.preventDefault();
        if (!isPhoneValid || !isDateValid || !userId) return;

        setIsSaving(true);
        const allowedFields = canManageTarget ? FULL_PERSONAL_FIELDS : SELF_EDITABLE_FIELDS;
        const cleanedData = Object.fromEntries(
            Object.entries(formData).filter(([fieldName]) => allowedFields.has(fieldName)),
        );
        if (cleanedData.date_of_birth === "") cleanedData.date_of_birth = null;

        try {
            await dispatch(updateUser({ userId, updates: cleanedData })).unwrap();
            await dispatch(fetchUserById(userId));
        } catch (error) {
            console.error(error);
        } finally {
            setIsSaving(false);
        }
    };

    if (!currentUser) return null;

    const formatDate = (value) => {
        if (!value) return "N/A";
        const date = new Date(value);
        return Number.isNaN(date.getTime()) ? "N/A" : date.toLocaleDateString("pt-PT");
    };

    if (!canViewFull) {
        return (
            <div className="pt-3">
                <div className="mb-4">
                    <h3 className="txt-main fw-bold">
                        {t("components.PersonalArea.general_info", "Informações gerais")}
                    </h3>
                    <p className="txt-subtle mb-4" style={{ fontSize: "0.9rem" }}>
                        {t("components.PersonalArea.public_info_desc", "Informações básicas do colaborador.")}
                    </p>
                </div>

                <Row className="g-4">
                    <Col md={4}>
                        <Form.Group>
                            <Form.Label className="txt-subtle fw-semibold" style={{ fontSize: "0.85rem" }}>
                                {t("components.PersonalArea.first_name", "Primeiro nome")}
                            </Form.Label>
                            <Form.Control value={currentUser.first_name || ""} readOnly className="bg-surface txt-main" />
                        </Form.Group>
                    </Col>
                    <Col md={4}>
                        <Form.Group>
                            <Form.Label className="txt-subtle fw-semibold" style={{ fontSize: "0.85rem" }}>
                                {t("components.PersonalArea.last_name", "Último nome")}
                            </Form.Label>
                            <Form.Control value={currentUser.last_name || ""} readOnly className="bg-surface txt-main" />
                        </Form.Group>
                    </Col>
                    <Col md={4}>
                        <Form.Group>
                            <Form.Label className="txt-subtle fw-semibold" style={{ fontSize: "0.85rem" }}>
                                {t("components.PersonalArea.dob", "Data de nascimento")}
                            </Form.Label>
                            <Form.Control value={formatDate(currentUser.date_of_birth)} readOnly className="bg-surface txt-main" />
                        </Form.Group>
                    </Col>
                    <Col md={4}>
                        <Form.Group>
                            <Form.Label className="txt-subtle fw-semibold" style={{ fontSize: "0.85rem" }}>
                                {t("components.PersonalArea.country", "País")}
                            </Form.Label>
                            <Form.Control value={currentUser.country || "N/A"} readOnly className="bg-surface txt-main" />
                        </Form.Group>
                    </Col>
                    <Col md={4}>
                        <Form.Group>
                            <Form.Label className="txt-subtle fw-semibold" style={{ fontSize: "0.85rem" }}>
                                {t("components.PersonalArea.city", "Cidade")}
                            </Form.Label>
                            <Form.Control value={currentUser.city || "N/A"} readOnly className="bg-surface txt-main" />
                        </Form.Group>
                    </Col>
                    <Col md={4}>
                        <Form.Group>
                            <Form.Label className="txt-subtle fw-semibold" style={{ fontSize: "0.85rem" }}>
                                {t("components.PersonalArea.delegation", "Delegação")}
                            </Form.Label>
                            <Form.Control
                                value={currentUser.delegation?.name || currentUser.delegation_name || "N/A"}
                                readOnly
                                className="bg-surface txt-main"
                            />
                        </Form.Group>
                    </Col>
                </Row>
            </div>
        );
    }

    return (
        <div className="pt-3">
            <div className="mb-5">
                <h3 className="txt-main fw-bold">{t('components.PersonalArea.general_info')}</h3>
                <p className="txt-subtle mb-4" style={{ fontSize: '0.9rem' }}>{t('components.PersonalArea.general_info_desc')}</p>
                <Form>
                    <Row className="mb-3 g-4">
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="txt-subtle fw-semibold" style={{ fontSize: '0.85rem' }}>{t('components.PersonalArea.first_name')}</Form.Label>
                                <Form.Control type="text" name="first_name" value={formData.first_name || ""} onChange={handleChange} className="bg-surface txt-main" disabled={!canEditField("first_name")} />
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="txt-subtle fw-semibold" style={{ fontSize: '0.85rem' }}>{t('components.PersonalArea.last_name')}</Form.Label>
                                <Form.Control type="text" name="last_name" value={formData.last_name || ""} onChange={handleChange} className="bg-surface txt-main" disabled={!canEditField("last_name")} />
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="txt-subtle fw-semibold" style={{ fontSize: '0.85rem' }}>{t('components.PersonalArea.phone')}</Form.Label>
                                <PhoneInput
                                    international
                                    defaultCountry="PT"
                                    value={formData.phone_number || ""}
                                    onChange={(val) => handlePhoneChange(val, 'phone_number')}
                                    className="form-control bg-surface txt-main"
                                    numberInputProps={{ className: "border-0" }}
                                    disabled={!canEditField("phone_number")}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className="mb-3 g-4">
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="txt-subtle fw-semibold" style={{ fontSize: '0.85rem' }}>{t('components.PersonalArea.dob')}</Form.Label>
                                <Form.Control type="date" name="date_of_birth" value={formData.date_of_birth || ""} onChange={handleChange} className="bg-surface txt-main" disabled={!canEditField("date_of_birth")} />
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="txt-subtle fw-semibold" style={{ fontSize: '0.85rem' }}>{t('components.PersonalArea.nationality')}</Form.Label>
                                <Form.Select name="nationality" value={formData.nationality || ""} onChange={handleChange} className="bg-surface txt-main cursor-pointer" disabled={!canEditField("nationality")}>
                                    <option value="" disabled>{t('common.select', 'Selecione...')}</option>
                                    {NATIONALITIES.map((nat) => <option key={nat} value={nat}>{nat}</option>)}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="txt-subtle fw-semibold" style={{ fontSize: '0.85rem' }}>{t('components.PersonalArea.emergency_number', 'Emergency Number')}</Form.Label>
                                <PhoneInput
                                    international
                                    defaultCountry="PT"
                                    value={formData.emergency_contact_phone || ""}
                                    onChange={(val) => handlePhoneChange(val, 'emergency_contact_phone')}
                                    className="form-control bg-surface txt-main"
                                    numberInputProps={{ className: "border-0" }}
                                    disabled={!canEditField("emergency_contact_phone")}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="txt-subtle fw-semibold" style={{ fontSize: '0.85rem' }}>
                                    {t('components.PersonalArea.delegation', 'Delegação')}
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    value={formData.delegation_name || 'N/A'}
                                    readOnly
                                    className="bg-surface txt-subtle"
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                </Form>
            </div>

            <div className="mb-5">
                <h3 className="txt-main fw-bold mb-1">{t('components.PersonalArea.bank_info')}</h3>
                <p className="txt-subtle mb-4" style={{ fontSize: '0.9rem' }}>{t('components.PersonalArea.bank_info_desc')}</p>
                <Form>
                    <Row className="mb-3 g-4">
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="txt-subtle fw-semibold" style={{ fontSize: '0.85rem' }}>{t('components.PersonalArea.account_format')}</Form.Label>
                                <Form.Control type="text" value="IBAN" readOnly className="bg-surface txt-subtle" disabled />
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="txt-subtle fw-semibold" style={{ fontSize: '0.85rem' }}>{t('components.PersonalArea.iban')}</Form.Label>
                                <Form.Control type="text" name="iban" value={formData.iban || ""} onChange={handleChange} className="bg-surface txt-main" disabled={!canEditField("iban")} />
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="txt-subtle fw-semibold" style={{ fontSize: '0.85rem' }}>{t('components.PersonalArea.swift_bic')}</Form.Label>
                                <Form.Control type="text" name="swift_bic" value={formData.swift_bic || ""} onChange={handleChange} className="bg-surface txt-main" disabled={!canEditField("swift_bic")} />
                            </Form.Group>
                        </Col>
                    </Row>
                </Form>
            </div>

            <div className="mb-5">
                <h3 className="txt-main fw-bold mb-1">{t('components.PersonalArea.id_info')}</h3>
                <p className="txt-subtle mb-4" style={{ fontSize: '0.9rem' }}>{t('components.PersonalArea.id_info_desc')}</p>
                <Form>
                    <Row className="mb-3 g-4">
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="txt-subtle fw-semibold" style={{ fontSize: '0.85rem' }}>{t('components.PersonalArea.id_number')}</Form.Label>
                                <Form.Control type="text" name="cc" value={formData.cc || ""} onChange={handleChange} className="bg-surface txt-main" disabled={!canEditField("cc")} />
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="txt-subtle fw-semibold" style={{ fontSize: '0.85rem' }}>{t('components.PersonalArea.ssn')}</Form.Label>
                                <Form.Control type="text" name="social_security_number" value={formData.social_security_number || ""} onChange={handleChange} className="bg-surface txt-main" disabled={!canEditField("social_security_number")} />
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="txt-subtle fw-semibold" style={{ fontSize: '0.85rem' }}>{t('components.PersonalArea.nif')}</Form.Label>
                                <Form.Control type="text" name="nif" value={formData.nif || ""} onChange={handleChange} className="bg-surface txt-main" disabled={!canEditField("nif")} />
                            </Form.Group>
                        </Col>
                    </Row>
                </Form>
            </div>

            <div className="mb-5">
                <h3 className="txt-main fw-bold mb-1">{t('components.PersonalArea.address_info')}</h3>
                <p className="txt-subtle mb-4" style={{ fontSize: '0.9rem' }}>{t('components.PersonalArea.address_info_desc')}</p>
                <Form>
                    <Row className="mb-3 g-4">
                        <Col md={12}>
                            <Form.Group>
                                <Form.Label className="txt-subtle fw-semibold" style={{ fontSize: '0.85rem' }}>{t('components.PersonalArea.address_line')}</Form.Label>
                                <Form.Control type="text" name="address" value={formData.address || ""} onChange={handleChange} className="bg-surface txt-main" disabled={!canEditField("address")} />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className="mb-3 g-4">
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label className="txt-subtle fw-semibold" style={{ fontSize: '0.85rem' }}>{t('components.PersonalArea.postal_code')}</Form.Label>
                                <Form.Control type="text" name="postal_code" value={formData.postal_code || ""} onChange={handleChange} className="bg-surface txt-main" disabled={!canEditField("postal_code")} />
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label className="txt-subtle fw-semibold" style={{ fontSize: '0.85rem' }}>{t('components.PersonalArea.city')}</Form.Label>
                                <Form.Control type="text" name="city" value={formData.city || ""} onChange={handleChange} className="bg-surface txt-main" disabled={!canEditField("city")} />
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label className="txt-subtle fw-semibold" style={{ fontSize: '0.85rem' }}>{t('components.PersonalArea.region')}</Form.Label>
                                <Form.Control type="text" name="region" value={formData.region || ""} onChange={handleChange} className="bg-surface txt-main" disabled={!canEditField("region")} />
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label className="txt-subtle fw-semibold" style={{ fontSize: '0.85rem' }}>{t('components.PersonalArea.country')}</Form.Label>
                                <Form.Select
                                    name="country"
                                    value={formData.country || ""}
                                    onChange={handleChange}
                                    className="bg-surface txt-main cursor-pointer"
                                    disabled={!canEditField("country")}
                                >
                                    <option value="" disabled>{t('common.select', 'Selecione...')}</option>
                                    {COUNTRIES.map((country) => (
                                        <option key={country} value={country}>{country}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                    </Row>
                    {canEdit && (
                        <Row>
                            <Col className="d-flex justify-content-end mt-4">
                                <Button
                                    type="button"
                                    variant="primary"
                                    className="rounded-pill"
                                    onClick={handleSave}
                                    disabled={isSaving || !isPhoneValid || !isDateValid}
                                    style={{ minWidth: '100px', minHeight: '40px', padding: '10px 20px', backgroundColor: 'var(--brand-primary)', border: 'none' }}
                                >
                                    {isSaving ? (
                                        <>
                                            <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                                            {t('common.saving', 'A guardar...')}
                                        </>
                                    ) : (
                                        t('common.save_changes', 'Guardar Alterações')
                                    )}
                                </Button>
                            </Col>
                        </Row>
                    )}
                </Form>
            </div>
        </div>
    );
};

export default PersonalDetailsTab;